import { saveAs } from 'file-saver';
import { kebabCase } from 'lodash';
import React from 'react';

import { echarts, ReactEChartsCore } from '../common/charts/echarts-core';
import { logger } from '../common/logger';

interface UseChartExportOptions {
    title: string;
    scope?: string;
}

const defaultExportHeight = 400;
const defaultExportWidth = 800;
const devicePixelRatio = 2;
const svgMimeType = 'image/svg+xml;charset=utf-8';

interface ChartInstance {
    getDom?: () => HTMLElement | undefined;
    getOption: () => echarts.EChartsCoreOption;
}

const createTempChart = (
    instance: ChartInstance,
    renderer: 'svg' | 'canvas'
): { chart: echarts.ECharts; container: HTMLDivElement } => {
    const dom = instance.getDom?.();
    const width = dom?.clientWidth ?? defaultExportWidth;
    const height = dom?.clientHeight ?? defaultExportHeight;
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '-9999px';
    container.style.width = `${width}px`;
    container.style.height = `${height}px`;
    document.body.appendChild(container);
    const chart = echarts.init(container, undefined, { height, renderer, width });
    const option = instance.getOption();
    option.animation = false;
    chart.setOption(option, true);
    chart.resize({ width, height });
    return { chart, container };
};

const disposeTempChart = (chart: echarts.ECharts | null, container: HTMLDivElement) => {
    chart?.dispose();
    if (container.parentNode) {
        container.parentNode.removeChild(container);
    }
};

const createSvgBlobFromDataUrl = (dataUrl: string) => {
    const commaIndex = dataUrl.indexOf(',');
    if (commaIndex === -1) {
        return new Blob([dataUrl], { type: svgMimeType });
    }
    const meta = dataUrl.slice(0, commaIndex);
    const payload = dataUrl.slice(commaIndex + 1);
    const isBase64 = meta.includes(';base64');
    const decoded = isBase64 ? atob(payload) : decodeURIComponent(payload);
    return new Blob([decoded], { type: svgMimeType });
};

export interface ChartExportHandlers {
    chartRef: React.MutableRefObject<ReactEChartsCore | null>;
    exportAsPng: () => Promise<void>;
    exportAsSvg: () => Promise<void>;
    isExporting: boolean;
}

export const useChartExport = (options: UseChartExportOptions): ChartExportHandlers => {
    const chartRef = React.useRef<ReactEChartsCore | null>(null);
    const [isExporting, setIsExporting] = React.useState(false);

    const slug = React.useMemo(() => {
        const source = [options.scope, options.title].filter(Boolean).join('-');
        return kebabCase(source) || 'chart';
    }, [options.scope, options.title]);

    const exportChart = React.useCallback(
        async (format: 'png' | 'svg') => {
            if (isExporting) {
                return;
            }
            const instance = chartRef.current?.getEchartsInstance?.();
            if (!instance) {
                logger.warn('Chart export skipped: missing echarts instance', { format, title: options.title });
                return;
            }
            setIsExporting(true);
            try {
                const { chart: tempChart, container } = createTempChart(instance, format === 'svg' ? 'svg' : 'canvas');
                try {
                    if (format === 'svg') {
                        const rawSvg =
                            typeof tempChart.renderToSVGString === 'function'
                                ? tempChart.renderToSVGString({ useViewBox: true })
                                : tempChart.getDataURL({ type: 'svg' });
                        const svgBlob = rawSvg.startsWith('data:')
                            ? createSvgBlobFromDataUrl(rawSvg)
                            : new Blob([rawSvg], { type: svgMimeType });
                        saveAs(svgBlob, `${slug}.svg`);
                    } else {
                        const dataUrl = tempChart.getDataURL({
                            backgroundColor: '#ffffff',
                            excludeComponents: ['toolbox'],
                            pixelRatio: devicePixelRatio,
                            type: 'png'
                        });
                        const response = await fetch(dataUrl);
                        const pngBlob = await response.blob();
                        saveAs(pngBlob, `${slug}.png`);
                    }
                } finally {
                    disposeTempChart(tempChart, container);
                }
            } catch (error) {
                logger.error('Chart export failed', { error, format, title: options.title });
            } finally {
                setIsExporting(false);
            }
        },
        [isExporting, options.title, slug]
    );

    const exportAsPng = React.useCallback(() => exportChart('png'), [exportChart]);
    const exportAsSvg = React.useCallback(() => exportChart('svg'), [exportChart]);

    return {
        chartRef,
        exportAsPng,
        exportAsSvg,
        isExporting
    };
};
